import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../WithdrawPage/withdrawal.css";
import "./security.css";
import "./accountConnections.css";

/** Backend expects `type: "phone"` for SMS; UI uses `"mobile"`. */
function toDisableAccountSubmitType(uiVerifyModal) {
  if (uiVerifyModal === "mobile") return "phone";
  return uiVerifyModal;
}

function toDisableAccountSendOtpChannel(uiChannel) {
  return uiChannel === "mobile" ? "phone" : "email";
}

const METHOD_PRIORITY = ["passkey", "totp", "email", "mobile"];

const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

function normalizeSecurityMethods(result) {
  const raw =
    result?.data?.security_methods ||
    result?.data?.data?.security_methods ||
    result?.security_methods ||
    result?.data?.securityMethods ||
    null;
  if (!raw || typeof raw !== "object") return null;
  return {
    email: !!raw.email,
    mobile: !!(raw.mobile ?? raw.phone ?? raw.sms),
    totp: !!raw.totp,
    passkey: !!raw.passkey,
  };
}

function pickPreferredVerificationMethod(methods) {
  if (!methods) return null;
  const hit = METHOD_PRIORITY.find((k) => methods[k]);
  return hit || null;
}

function parseConnectedAccountsPayload(result) {
  const raw = result?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.accounts)) return raw.accounts;
  return [];
}

function connectionByType(list, t) {
  const want = String(t).toLowerCase();
  return list.find((x) => String(x?.type ?? "").toLowerCase() === want) || null;
}

/** API: `status` is `"connected"` | `"disconnected"`. If omitted, treat row as connected (legacy). */
function isOAuthAccountConnected(entry) {
  if (!entry) return false;
  const raw = entry.status;
  if (raw === undefined || raw === null || String(raw).trim() === "") return true;
  return String(raw).toLowerCase() === "connected";
}

function oauthAccountPrompt(provider) {
  return provider === "apple" ? "Connect Apple account" : "Connect Google account";
}

/** When disconnected (or missing), show connect prompt only — no email. When connected, show email or fallback label. */
function oauthAccountMetaLine(account, provider) {
  if (!account || !isOAuthAccountConnected(account)) {
    return oauthAccountPrompt(provider);
  }
  const email = String(account.email ?? "").trim();
  if (email) return email;
  return "Connected";
}

function ChooseVerificationMethodModal({ methods, onClose, onPick, onSecurityUnavailable }) {
  return (
    <div className="withdraw_confirm_overlay pm-choose-method-overlay-stacked" role="presentation">
      <div className="withdraw_choose_modal pm-choose-method-modal" role="dialog" aria-modal="true" aria-labelledby="acct-ch-title">
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="acct-ch-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button key={key} type="button" className="withdraw_choose_item pm-choose-method-item" onClick={() => onPick(key)}>
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const GoogleLogo = () => (
  <svg width="36" height="36" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const AppleLogo = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="#101828" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/**
 * Account Connections — GET linked OAuth accounts + verification; complete via POST connect-disconnect-account.
 * Route: /user_profile/security/accountConnections
 */
const AccountConnections = () => {
  const navigate = useNavigate();
  const { userDetails } = useContext(ProfileContext);

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");

  const maskedEmail = useMemo(() => maskEmailForDisplay(accountEmail), [accountEmail]);
  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  const pendingFlowRef = useRef(null);

  const [view, setView] = useState("main");
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);

  const [unavailEmailSelected, setUnavailEmailSelected] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetAck, setResetAck] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [newEmailCode, setNewEmailCode] = useState("");

  const [resolvedMethods, setResolvedMethods] = useState(null);
  const [methodsPrefetchLoading, setMethodsPrefetchLoading] = useState(true);
  const [methodsPrefetchError, setMethodsPrefetchError] = useState("");
  const [verifyModal, setVerifyModal] = useState(null);
  const [chooseMethodsOpen, setChooseMethodsOpen] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCells, setOtpCells] = useState(["", "", "", "", "", ""]);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [totpCode, setTotpCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyAlternateEligible, setPasskeyAlternateEligible] = useState(false);

  const googleAccount = useMemo(() => connectionByType(connections, "google"), [connections]);
  const appleAccount = useMemo(() => connectionByType(connections, "apple"), [connections]);

  const googleConnected = useMemo(() => isOAuthAccountConnected(googleAccount), [googleAccount]);
  const appleConnected = useMemo(() => isOAuthAccountConnected(appleAccount), [appleAccount]);

  const googleMeta = useMemo(() => oauthAccountMetaLine(googleAccount, "google"), [googleAccount]);
  const appleMeta = useMemo(() => oauthAccountMetaLine(appleAccount, "apple"), [appleAccount]);

  const fetchConnectedAccounts = useCallback(async () => {
    setConnectionsLoading(true);
    try {
      const result = await AuthService.getConnectedAccounts();
      if (!result?.success) {
        alertErrorMessage(result?.message || "Could not load connected accounts.");
        setConnections([]);
        return;
      }
      setConnections(parseConnectedAccountsPayload(result));
    } catch (e) {
      alertErrorMessage(e?.message || "Could not load connected accounts.");
      setConnections([]);
    } finally {
      setConnectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  const prefetchSecurityMethods = useCallback(async () => {
    setMethodsPrefetchLoading(true);
    setMethodsPrefetchError("");
    try {
      const result = await AuthService.getSecurityMethodsList();
      const m = normalizeSecurityMethods(result);
      if (!result?.success || !m) {
        setResolvedMethods(null);
        setMethodsPrefetchError(result?.message || "Could not load security methods.");
        return;
      }
      setResolvedMethods(m);
    } catch (e) {
      setResolvedMethods(null);
      setMethodsPrefetchError(e?.message || "Network error. Please try again.");
    } finally {
      setMethodsPrefetchLoading(false);
    }
  }, []);

  useEffect(() => {
    void prefetchSecurityMethods();
  }, [prefetchSecurityMethods]);

  const hasVerificationMethod = useMemo(
    () => resolvedMethods != null && pickPreferredVerificationMethod(resolvedMethods) != null,
    [resolvedMethods]
  );

  const resetVerificationInputs = useCallback(() => {
    setOtpCells(["", "", "", "", "", ""]);
    setTotpCode("");
  }, []);

  const closeVerifyFlow = useCallback(() => {
    pendingFlowRef.current = null;
    setVerifyModal(null);
    setChooseMethodsOpen(false);
    setPasskeyAlternateEligible(false);
    resetVerificationInputs();
  }, [resetVerificationInputs]);

  const goSecurityUnavailable = () => navigate("/asset_managemnet/security_verification_unavailable");

  const runOAuthVerifiedRequest = useCallback(
    async ({ type, code = "", credential }) => {
      const flow = pendingFlowRef.current;
      if (!flow) {
        alertErrorMessage("Session expired. Please try again.");
        return false;
      }
      const action = flow.intent === "connect" ? "connected" : "disconnected";
      const payload = { action, account_type: flow.provider, type, code, credential };
      try {
        const res = await AuthService.connectDisconnectAccount(payload);
        if (res?.success) {
          alertSuccessMessage(res.message || "Account connection updated.");
          pendingFlowRef.current = null;
          closeVerifyFlow();
          await fetchConnectedAccounts();
          return true;
        }
        alertErrorMessage(res?.message || "Verification failed.");
        return false;
      } catch (e) {
        alertErrorMessage(e?.message || "Verification failed.");
        return false;
      }
    },
    [closeVerifyFlow, fetchConnectedAccounts]
  );

  /** @returns {Promise<"success"|"cancelled"|"failed"|"invalid">} */
  const verifyPasskey = useCallback(
    async (opts = {}) => {
      const { silentCancel = false } = opts;
      const signId = accountEmail || accountPhoneDigits;
      if (!signId) {
        alertErrorMessage("Account identifier missing for passkey verification.");
        return "invalid";
      }
      setPasskeyAlternateEligible(false);
      setPasskeyBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        const optionsResult = await AuthService.passkeyGetAuthOptions(signId);
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        let credential;
        LoaderHelper.loaderStatus(false);
        try {
          credential = await startAuthentication(optionsResult.data);
        } catch (webauthnError) {
          LoaderHelper.loaderStatus(false);
          if (webauthnError?.name === "NotAllowedError") {
            if (!silentCancel) {
              alertErrorMessage("Authentication was cancelled or timed out. Try again or use another method.");
            }
            setPasskeyAlternateEligible(true);
            return "cancelled";
          }
          alertErrorMessage("Passkey verification failed.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        LoaderHelper.loaderStatus(true);
        const ok = await runOAuthVerifiedRequest({ type: "passkey", code: "", credential });
        if (ok) return "success";
        setPasskeyAlternateEligible(true);
        return "failed";
      } catch (e) {
        alertErrorMessage(e?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } finally {
        setPasskeyBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [accountEmail, accountPhoneDigits, runOAuthVerifiedRequest]
  );

  const alternateMethodKeys = useMemo(() => {
    if (!resolvedMethods || !verifyModal) return [];
    return METHOD_PRIORITY.filter((k) => resolvedMethods[k] && k !== verifyModal);
  }, [resolvedMethods, verifyModal]);

  const sendConnectDisconnectOtp = useCallback(async (target) => {
    if (otpResendTimer > 0) return false;
    const security_methods = toDisableAccountSendOtpChannel(target);
    setOtpSending(true);
    try {
      const result = await AuthService.sendOtpForConnectDisconnectAccount(security_methods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        setOtpResendTimer(60);
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, [otpResendTimer]);

  useEffect(() => {
    if (otpResendTimer <= 0) return undefined;
    const t = setTimeout(() => setOtpResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendTimer]);

  const handleOtpInput = (idx, e) => {
    const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 1);
    setOtpCells((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) {
      document.getElementById(`acct-daev-code-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otpCells[idx] && idx > 0) {
      document.getElementById(`acct-daev-code-${idx - 1}`)?.focus();
    }
  };

  const handleInputPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = String(pasteData || "").replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length > 0) {
      setOtpCells((prev) => {
        const next = [...prev];
        digits.forEach((d, i) => {
          if (i < 6) next[i] = d;
        });
        return next;
      });
      window.requestAnimationFrame(() => {
        const targetIdx = digits.length === 6 ? 5 : digits.length;
        document.getElementById(`acct-daev-code-${targetIdx}`)?.focus();
      });
    }
  };

  const handlePasteOtpBtn = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length < 6) return;
      setOtpCells([digits[0], digits[1], digits[2], digits[3], digits[4], digits[5]]);
      document.getElementById("acct-daev-code-5")?.focus();
    } catch {
      // silent
    }
  };

  const openChooseMethods = useCallback(() => {
    if (alternateMethodKeys.length === 0) {
      alertErrorMessage("No other verification method is available.");
      return;
    }
    setChooseMethodsOpen(true);
  }, [alternateMethodKeys.length]);

  const applyVerificationMethod = useCallback(
    async (method) => {
      setChooseMethodsOpen(false);
      resetVerificationInputs();
      if (method === "passkey") {
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }
      setVerifyModal(method);
    },
    [resetVerificationInputs, verifyPasskey, closeVerifyFlow]
  );

  const startOAuthVerification = async (intent, provider) => {
    if (flowBusy || passkeyBusy || methodsPrefetchLoading) return;
    if (methodsPrefetchError && !resolvedMethods) {
      alertErrorMessage(methodsPrefetchError);
      return;
    }
    if (!hasVerificationMethod) {
      alertErrorMessage("Please add a verification method first");
      return;
    }
    pendingFlowRef.current = { intent, provider: String(provider).toLowerCase() };
    const preferred = pickPreferredVerificationMethod(resolvedMethods);
    if (!preferred) return;

    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      resetVerificationInputs();

      if (preferred === "passkey") {
        setChooseMethodsOpen(false);
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }

      setVerifyModal(preferred);
    } catch (e) {
      alertErrorMessage(e?.message || "Something went wrong.");
      pendingFlowRef.current = null;
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyTotpSubmit = async () => {
    const code = String(totpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return;
    }
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      await runOAuthVerifiedRequest({ type: "totp", code, credential: "" });
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyEmailOrMobileSubmit = async () => {
    const code = otpCells.join("");
    if (code.length !== 6) return;
    const type = toDisableAccountSubmitType(verifyModal);
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      await runOAuthVerifiedRequest({ type, code, credential: "" });
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePasteTotp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) setTotpCode(digits);
    } catch {
      // ignore
    }
  };

  const verificationUiOpen = verifyModal != null || chooseMethodsOpen;
  const showChooser = chooseMethodsOpen && verifyModal;

  const isUnavailConfirmEnabled = unavailEmailSelected;
  const isResetConfirmEnabled = resetAck;
  const isAddSubmitEnabled = useMemo(() => {
    const e = newEmail.trim();
    return e.length > 3 && e.includes("@") && newEmailCode.trim().length > 0;
  }, [newEmail, newEmailCode]);

  useEffect(() => {
    if (view !== "main") return undefined;
    const block = verificationUiOpen || resetModalOpen;
    if (!block) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (verificationUiOpen) closeVerifyFlow();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [view, verificationUiOpen, resetModalOpen, closeVerifyFlow]);

  const handleSecurityUnavailableFromChooser = () => {
    closeVerifyFlow();
    setView("unavailable");
    setUnavailEmailSelected(false);
  };

  const handleUnavailConfirm = () => {
    if (!isUnavailConfirmEnabled) return;
    setResetModalOpen(true);
    setResetAck(false);
  };

  const handleResetConfirm = () => {
    if (!isResetConfirmEnabled) return;
    setResetModalOpen(false);
    setResetAck(false);
    setView("addEmail");
    setNewEmail("");
    setNewEmailCode("");
  };

  const handleAddSubmit = () => {
    if (!isAddSubmitEnabled) return;
    setView("main");
    navigate("/user_profile/security", { replace: true });
  };

  const crumbActive =
    view === "main"
      ? "Account Connections"
      : view === "unavailable"
        ? "Select Unavailable Methods"
        : "Add Email";

  return (
    <main className="acct-page" aria-labelledby="acct-title">
      <nav className="acct-page__crumbs" aria-label="Breadcrumb">
        <ol className="acct-page__crumbList">
          <li className="acct-page__crumbItem">
            <button type="button" className="acct-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="acct-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="acct-page__crumbItem acct-page__crumbItem--active" aria-current="page">
            {crumbActive}
          </li>
        </ol>
      </nav>

      {view === "main" ? (
        <>
          <div className="acct-list">
            <h1 id="acct-title" className="acct-page__title">
              Account Connections
            </h1>

            {methodsPrefetchLoading ? (
              <p className="acct-page__hint" role="status">
                Checking your security methods…
              </p>
            ) : null}

            {!methodsPrefetchLoading && methodsPrefetchError ? (
              <div className="acct-page__banner" role="alert">
                <p>{methodsPrefetchError}</p>
                <button type="button" className="acct-page__bannerBtn" onClick={() => void prefetchSecurityMethods()}>
                  Retry
                </button>
              </div>
            ) : null}

            <div className="acct-card">
              <div className="acct-card__left">
                <div className="acct-card__logo">
                  <GoogleLogo />
                </div>
                <div className="acct-card__body">
                  <p className="acct-card__title">Sign in with Google</p>
                  <p className="acct-card__meta">
                    {connectionsLoading ? "Loading…" : googleMeta}
                  </p>
                </div>
              </div>
              {googleConnected ? (
                <button
                  type="button"
                  className="acct-card__btn acct-card__btn--disconnect"
                  disabled={flowBusy || passkeyBusy || methodsPrefetchLoading || !hasVerificationMethod}
                  onClick={() => void startOAuthVerification("disconnect", "google")}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="acct-card__btn acct-card__btn--connect"
                  disabled={flowBusy || passkeyBusy || methodsPrefetchLoading || !hasVerificationMethod}
                  onClick={() => void startOAuthVerification("connect", "google")}
                >
                  Connect
                </button>
              )}
            </div>

            <div className="acct-card">
              <div className="acct-card__left">
                <div className="acct-card__logo">
                  <AppleLogo />
                </div>
                <div className="acct-card__body">
                  <p className="acct-card__title">Sign in with Apple</p>
                  <p className="acct-card__meta">
                    {connectionsLoading ? "Loading…" : appleMeta}
                  </p>
                </div>
              </div>
              {appleConnected ? (
                <button
                  type="button"
                  className="acct-card__btn acct-card__btn--disconnect"
                  disabled={flowBusy || passkeyBusy || methodsPrefetchLoading || !hasVerificationMethod}
                  onClick={() => void startOAuthVerification("disconnect", "apple")}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="acct-card__btn acct-card__btn--connect"
                  disabled={flowBusy || passkeyBusy || methodsPrefetchLoading || !hasVerificationMethod}
                  onClick={() => void startOAuthVerification("connect", "apple")}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        </>
      ) : view === "unavailable" ? (
        <div className="acct-unavail">
          <h1 id="acct-title" className="acct-unavail__title">
            Select Unavailable Methods
          </h1>
          <p className="acct-unavail__sub">
            Please select ALL the security methods that are unavailable and you would like to reset
          </p>

          <button
            type="button"
            className={`acct-unavail__choice ${unavailEmailSelected ? "is-selected" : ""}`}
            onClick={() => setUnavailEmailSelected(true)}
          >
            <div className="acct-unavail__choiceLeft">
              <span className="acct-unavail__choiceIcon" aria-hidden="true">
                <i className="ri-mail-line" />
              </span>
              <div>
                <div className="acct-unavail__choiceTitle">Email unavailable?</div>
                <div className="acct-unavail__choiceSub">Reset j***9@gmail.com</div>
              </div>
            </div>
            <span className="acct-unavail__radio" aria-hidden="true" />
          </button>

          <button
            type="button"
            className={`acct-unavail__confirm ${isUnavailConfirmEnabled ? "is-enabled" : ""}`}
            disabled={!isUnavailConfirmEnabled}
            onClick={handleUnavailConfirm}
          >
            Confirm
          </button>

          <button type="button" className="acct-unavail__how" onClick={() => navigate("/user_profile/support")}>
            How does this work?
          </button>
        </div>
      ) : (
        <div className="acct-add">
          <h1 id="acct-title" className="acct-add__title">
            Add Email
          </h1>
          <p className="acct-add__sub">
            Add your email address to receive important notifications and enhance your account security.
          </p>

          <div className="acct-field">
            <label className="acct-label" htmlFor="acct-new-email">
              New Email Address
            </label>
            <input
              id="acct-new-email"
              className="acct-input"
              placeholder=""
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>

          <div className="acct-field">
            <label className="acct-label" htmlFor="acct-new-code">
              Enter Verification Code
            </label>
            <div className="acct-inputWrap">
              <input
                id="acct-new-code"
                className="acct-input acct-input--code"
                placeholder=""
                value={newEmailCode}
                onChange={(e) => setNewEmailCode(e.target.value)}
              />
              <button type="button" className="acct-getCode">
                Get Code
              </button>
            </div>
          </div>

          <button
            type="button"
            className={`acct-add__submit ${isAddSubmitEnabled ? "is-enabled" : ""}`}
            disabled={!isAddSubmitEnabled}
            onClick={handleAddSubmit}
          >
            Submit
          </button>
        </div>
      )}

      {verifyModal === "passkey" && !showChooser && view === "main" ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation">
          <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_passkey_title">Verify with passkey</div>
            <div className="withdraw_passkey_hero" aria-hidden="true">
              <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
              <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
            </div>
            <div className="withdraw_passkey_text">Complete verification using your passkey</div>
            <div className="withdraw_passkey_subtext">Please follow the instructions on your browser to complete verification.</div>
            <button type="button" className="withdraw_passkey_verify" disabled={passkeyBusy} onClick={() => void verifyPasskey({ silentCancel: false })}>
              {passkeyBusy ? "Verifying…" : "Verify"}
            </button>
            {alternateMethodKeys.length > 0 && passkeyAlternateEligible ? (
              <button type="button" className="withdraw_passkey_link" onClick={openChooseMethods}>
                Use another verification method
              </button>
            ) : null}
            <button type="button" className="withdraw_passkey_link" onClick={goSecurityUnavailable}>
              Security verification unavailable?
            </button>
            <div className="withdraw_passkey_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {verifyModal === "totp" && !showChooser && view === "main" ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
          <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_auth_title">Authenticator App Verification</div>
            <div className="withdraw_auth_subtitle">
              Enter the 6-digit code generated by the
              <br />
              Authenticator App.
            </div>
            <div className="withdraw_auth_label">Authenticator App</div>
            <div className="withdraw_auth_input_row">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder=""
                value={totpCode}
                onChange={(e) => setTotpCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="withdraw_auth_paste" onClick={() => void handlePasteTotp()}>
                Paste
              </button>
            </div>
            <button type="button" className="withdraw_auth_submit" disabled={flowBusy} onClick={() => void verifyTotpSubmit()}>
              {flowBusy ? "Verifying…" : "Submit"}
            </button>
            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="withdraw_auth_link" onClick={openChooseMethods}>
                  Switch to Another Verification Method
                </button>
              ) : null}
              <button type="button" className="withdraw_auth_link" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>
            <div className="withdraw_passkey_footer withdraw_auth_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {(verifyModal === "email" || verifyModal === "mobile") && !showChooser && view === "main" ? (
        <div className="tf-sec-page__daev-overlay" role="presentation">
          <div
            className="tf-sec-page__daev-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="acct-daev-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="tf-sec-page__daev-close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>

            <h2 id="acct-daev-title" className="tf-sec-page__daev-title">
              {verifyModal === "mobile" ? "Mobile Verification" : "Email Verification"}
            </h2>
            <p className="tf-sec-page__daev-desc">
              {verifyModal === "mobile"
                ? `Tap Get Code to receive a 6-digit OTP at ${maskedPhone}, then enter it below.`
                : `Tap Get Code to receive a 6-digit OTP at ${maskedEmail}, then enter it below.`}
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Verification code" style={{ marginTop: "20px" }}>
              {otpCells.map((digit, idx) => (
                <input
                  key={idx}
                  id={`acct-daev-code-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={idx === 0 ? "one-time-code" : undefined}
                  aria-label={`Digit ${idx + 1}`}
                  onInput={(e) => handleOtpInput(idx, e)}
                  onChange={(e) => handleOtpInput(idx, e)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  onPaste={handleInputPaste}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links" style={{ marginTop: "20px", marginBottom: "26px" }}>
              <button
                type="button"
                className="passkey-verify-modal__link"
                onClick={() => void sendConnectDisconnectOtp(verifyModal)}
                disabled={otpResendTimer > 0 || otpSending}
                style={otpResendTimer > 0 ? { opacity: 0.55, cursor: "not-allowed", textDecoration: "none" } : undefined}
              >
                {otpResendTimer > 0 ? `Resend in ${otpResendTimer}s` : "Resend"}
              </button>
              {/* <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePasteOtpBtn}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button> */}
            </div>

            <button
              type="button"
              className="tf-sec-page__daev-submit"
              disabled={otpCells.join("").length !== 6 || flowBusy}
              onClick={() => void verifyEmailOrMobileSubmit()}
            >
              {flowBusy ? "Verifying…" : "Submit"}
            </button>

            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="tf-sec-page__daev-help" onClick={openChooseMethods}>
                  Switch verification method
                </button>
              ) : null}
              <button type="button" className="tf-sec-page__daev-help" onClick={handleSecurityUnavailableFromChooser}>
                Security verification unavailable?
              </button>
            </div>

            <div className="tf-sec-page__daev-footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {showChooser && view === "main" ? (
        <ChooseVerificationMethodModal
          methods={alternateMethodKeys}
          onClose={() => setChooseMethodsOpen(false)}
          onPick={(key) => void applyVerificationMethod(key)}
          onSecurityUnavailable={goSecurityUnavailable}
        />
      ) : null}

      {resetModalOpen ? (
        <div className="acct-modalOverlay" role="presentation">
          <div className="acct-resetModal" role="dialog" aria-modal="true" aria-labelledby="acct-reset-title">
            <button
              type="button"
              className="acct-resetModal__close"
              aria-label="Close"
              onClick={() => {
                setResetModalOpen(false);
                setResetAck(false);
              }}
            >
              ×
            </button>

            <div className="acct-resetModal__art">
              <img className="dark_img" src="/images/security/confirm _reset_vector.svg" alt="reset" />
              <img className="light_img" src="/images/security/confirm _reset_vector_light.svg" alt="reset" />
            </div>

            <h2 id="acct-reset-title" className="acct-resetModal__title">
              Confirm Reset of Security Methods
            </h2>
            <p className="acct-resetModal__sub">
              Resetting your security methods will remove current settings and may temporarily restrict some features.
            </p>

            <div className="acct-resetModal__box">
              <label className="acct-resetModal__row">
                <input type="checkbox" checked={resetAck} onChange={(e) => setResetAck(e.target.checked)} />
                <span className="acct-resetModal__warnText">
                  For your account&apos;s protection, certain services such as withdrawals, P2P selling, and payment features may be
                  temporarily restricted for 48-72 hours after you make this change. This security measure helps prevent
                  unauthorized access and ensures the safety of your funds during sensitive updates.
                </span>
              </label>
            </div>

            <div className="acct-resetModal__actions">
              <button
                type="button"
                className="acct-resetModal__cancel"
                onClick={() => {
                  setResetModalOpen(false);
                  setResetAck(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`acct-resetModal__confirm ${isResetConfirmEnabled ? "is-enabled" : ""}`}
                disabled={!isResetConfirmEnabled}
                onClick={handleResetConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default AccountConnections;
